import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Container, Badge, Card, Alert, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import NavigationBar from '../NavigationBar';

// Who may use the FlowTech MCP connector (Claude / Grok), and which apps are
// currently connected. "Default" = write for admins, no access for others.
const ACCESS_OPTIONS = [
    { value: 'default', label: 'Default' },
    { value: 'none', label: 'No access' },
    { value: 'read', label: 'Read only' },
    { value: 'write', label: 'Read & write' },
];

const ACCESS_BADGE = { none: 'secondary', read: 'info', write: 'success' };

// Read tools only look things up; write tools create or change data.
const TOOL_TYPE_STYLE = {
    read: { accent: '#198754', background: '#f3faf6' },
    write: { accent: '#fd7e14', background: '#fff8f0' },
};

function McpAccess() {
    const [users, setUsers] = useState([]);
    const [connections, setConnections] = useState([]);
    const [tools, setTools] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [error, setError] = useState('');

    // Tool restrictions window, for one person or one department.
    // target = { kind: 'user' | 'department', id, name, user? }.
    // toolSets.blocked = tools blocked for the target itself; for a person,
    // toolSets.allowed = exceptions (allowed although their department blocks
    // them) and departmentBlocked = what their department blocks.
    // Everything not blocked is allowed.
    const [toolsTarget, setToolsTarget] = useState(null);
    const [toolSets, setToolSets] = useState({ blocked: new Set(), allowed: new Set() });
    const [departmentBlocked, setDepartmentBlocked] = useState(new Set());
    const [toolsDepartmentName, setToolsDepartmentName] = useState(null);
    const [toolSearch, setToolSearch] = useState('');
    const [toolsLoading, setToolsLoading] = useState(false);
    const [toolsSaving, setToolsSaving] = useState(false);

    const [showKeyModal, setShowKeyModal] = useState(false);
    const [keyUserId, setKeyUserId] = useState('');
    const [keyName, setKeyName] = useState('');
    const [createdKey, setCreatedKey] = useState('');

    const [connectionToRevoke, setConnectionToRevoke] = useState(null);

    useEffect(() => {
        fetchAll();
    }, []);

    const fetchAll = async () => {
        try {
            const [usersRes, connectionsRes, toolsRes, departmentsRes] = await Promise.all([
                axios.get(`${window.backendUrl}/mcp-admin/users`, { withCredentials: true }),
                axios.get(`${window.backendUrl}/mcp-admin/connections`, { withCredentials: true }),
                axios.get(`${window.backendUrl}/mcp-admin/tools`, { withCredentials: true }),
                axios.get(`${window.backendUrl}/mcp-admin/departments`, { withCredentials: true }),
            ]);
            setUsers(usersRes.data);
            setConnections(connectionsRes.data);
            setTools(toolsRes.data);
            setDepartments(departmentsRes.data);
            setError('');
        } catch (err) {
            console.error('Error loading MCP access', err);
            setError('Failed to load MCP access data.');
        }
    };

    const handleAccessChange = async (userId, accessLevel) => {
        try {
            await axios.put(`${window.backendUrl}/mcp-admin/users/${userId}/access`, { accessLevel }, { withCredentials: true });
            fetchAll();
        } catch (err) {
            alert('Failed to update access');
        }
    };

    // Takes the person's Telegram off their account: no Flow chat or alerts until they Connect Telegram again.
    const handleTelegramUnlink = async (user) => {
        if (!window.confirm(`Unlink Telegram from ${user.Username}? They lose Flow and alerts until they connect again.`)) return;
        try {
            await axios.post(`${window.backendUrl}/telegram/admin/unlink/${user.UserID}`, {}, { withCredentials: true });
            fetchAll();
        } catch (err) {
            alert('Failed to unlink Telegram');
        }
    };

    const openToolsModal = async (kind, item) => {
        const isUser = kind === 'user';
        setToolsTarget({
            kind,
            id: isUser ? item.UserID : item.DepartmentID,
            name: isUser ? item.Username : item.Name,
            user: isUser ? item : null,
        });
        setToolSearch('');
        setToolSets({ blocked: new Set(), allowed: new Set() });
        setDepartmentBlocked(new Set());
        setToolsDepartmentName(null);
        setToolsLoading(true);
        try {
            const url = isUser
                ? `${window.backendUrl}/mcp-admin/users/${item.UserID}/tools`
                : `${window.backendUrl}/mcp-admin/departments/${item.DepartmentID}/tools`;
            const res = await axios.get(url, { withCredentials: true });
            setToolSets({ blocked: new Set(res.data.blocked), allowed: new Set(res.data.allowed || []) });
            if (isUser) {
                setDepartmentBlocked(new Set(res.data.departmentBlocked || []));
                setToolsDepartmentName(res.data.departmentName);
            }
        } catch (err) {
            alert('Failed to load tool access');
            setToolsTarget(null);
        } finally {
            setToolsLoading(false);
        }
    };

    // What the target's tool access is when nothing is set on the target
    // itself: a person inherits their department, a department starts open.
    const inheritedAllowed = (name) => !departmentBlocked.has(name);

    const isToolAllowed = (name) => {
        if (toolSets.blocked.has(name)) return false;
        if (toolSets.allowed.has(name)) return true;
        return inheritedAllowed(name);
    };

    // Sets one tool to allowed / blocked. Only a difference from the inherited
    // state is stored, so there are never redundant overrides.
    const setToolAllowed = (sets, name, allowed) => {
        const blocked = new Set(sets.blocked);
        const exceptions = new Set(sets.allowed);
        blocked.delete(name);
        exceptions.delete(name);
        if (allowed !== inheritedAllowed(name)) {
            if (allowed) exceptions.add(name); else blocked.add(name);
        }
        return { blocked, allowed: exceptions };
    };

    const toggleTool = (name) => {
        setToolSets(prev => setToolAllowed(prev, name, !isToolAllowed(name)));
    };

    // Allow or block every tool of one type (read / write) at once.
    const setAllOfType = (type, allowed) => {
        setToolSets(prev => tools.filter(t => t.type === type).reduce((sets, t) => setToolAllowed(sets, t.name, allowed), prev));
    };

    const saveTools = async () => {
        setToolsSaving(true);
        try {
            if (toolsTarget.kind === 'user') {
                await axios.put(`${window.backendUrl}/mcp-admin/users/${toolsTarget.id}/tools`,
                    { blocked: [...toolSets.blocked], allowed: [...toolSets.allowed] }, { withCredentials: true });
            } else {
                await axios.put(`${window.backendUrl}/mcp-admin/departments/${toolsTarget.id}/tools`,
                    { blocked: [...toolSets.blocked] }, { withCredentials: true });
            }
            setToolsTarget(null);
            fetchAll();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to save tool access');
        } finally {
            setToolsSaving(false);
        }
    };

    const isUserTarget = toolsTarget?.kind === 'user';
    const targetAccess = toolsTarget?.user?.EffectiveAccessLevel;
    const blockedNowCount = tools.filter(t => !isToolAllowed(t.name)).length;

    const confirmRevoke = async () => {
        try {
            await axios.post(`${window.backendUrl}/mcp-admin/connections/${connectionToRevoke.TokenId}/revoke`, {}, { withCredentials: true });
            setConnectionToRevoke(null);
            fetchAll();
        } catch (err) {
            alert('Failed to revoke connection');
        }
    };

    const openKeyModal = () => {
        setKeyUserId('');
        setKeyName('');
        setCreatedKey('');
        setShowKeyModal(true);
    };

    const handleCreateKey = async () => {
        try {
            const res = await axios.post(`${window.backendUrl}/mcp-admin/keys`, { userId: keyUserId, name: keyName }, { withCredentials: true });
            setCreatedKey(res.data.key);
            fetchAll();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to create key');
        }
    };

    const closeKeyModal = () => {
        setShowKeyModal(false);
        setCreatedKey('');
    };

    const formatDate = (value) => (value ? String(value).replace('T', ' ').slice(0, 16) : '—');

    return (
        <>
            <NavigationBar/>
            <Container className="mt-4 mb-5">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h3>MCP Access</h3>
                    <Button variant="primary" onClick={openKeyModal}>Create Personal Key</Button>
                </div>
                <p className="text-muted small mb-3">
                    Controls who can connect Claude or Grok to FlowTech (connector URL: <code>{window.backendUrl}/mcp</code>).
                    Users sign in once with their FlowTech username and password. Read-only users can't see or run tools that create or change data.
                </p>
                {error && <Alert variant="danger">{error}</Alert>}

                <Card className="shadow-sm mb-4">
                    <Card.Header><strong>Users</strong></Card.Header>
                    <Table hover responsive className="mb-0">
                        <thead className="bg-light text-center">
                            <tr>
                                <th>S.L</th>
                                <th>Username</th>
                                <th>Role</th>
                                <th>Department</th>
                                <th>MCP Access</th>
                                <th>Effective</th>
                                <th>Tools</th>
                                <th>Telegram</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u, index) => (
                                <tr key={u.UserID} className="text-center align-middle">
                                    <td>{index + 1}</td>
                                    <td>{u.Username}</td>
                                    <td>
                                        <Badge bg={u.Role === 'admin' ? 'danger' : 'success'}>
                                            {(u.Role || 'user').toUpperCase()}
                                        </Badge>
                                    </td>
                                    <td>{u.DepartmentName || <span className="text-muted">—</span>}</td>
                                    <td style={{ minWidth: 160 }}>
                                        <Form.Select size="sm" value={u.AccessLevel} onChange={e => handleAccessChange(u.UserID, e.target.value)}>
                                            {ACCESS_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </Form.Select>
                                    </td>
                                    <td>
                                        <Badge bg={ACCESS_BADGE[u.EffectiveAccessLevel]}>
                                            {ACCESS_OPTIONS.find(opt => opt.value === u.EffectiveAccessLevel)?.label}
                                        </Badge>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <span className={u.BlockedToolCount ? 'text-danger me-2' : 'text-muted me-2'}>
                                            {u.BlockedToolCount ? `${u.BlockedToolCount} blocked` : 'All tools'}
                                        </span>
                                        <Button size="sm" variant="outline-primary" onClick={() => openToolsModal('user', u)}>Tools</Button>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {u.TelegramId ? (
                                            <>
                                                <Badge bg="success" className="me-2" title={`Telegram ID ${u.TelegramId}`}>Linked</Badge>
                                                <Button size="sm" variant="outline-danger" onClick={() => handleTelegramUnlink(u)}>Unlink</Button>
                                            </>
                                        ) : <span className="text-muted">—</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>

                <Card className="shadow-sm mb-4">
                    <Card.Header>
                        <strong>Departments</strong>
                        <span className="text-muted small ms-2">
                            Tools blocked here are blocked for every member. Use a person's own Tools window to make an exception for them.
                        </span>
                    </Card.Header>
                    <Table hover responsive className="mb-0">
                        <thead className="bg-light text-center">
                            <tr>
                                <th>Department</th>
                                <th>Members</th>
                                <th>Tools</th>
                            </tr>
                        </thead>
                        <tbody>
                            {departments.length === 0 && (
                                <tr><td colSpan={3} className="text-center text-muted">No departments yet — add them in Manage Department</td></tr>
                            )}
                            {departments.map(d => (
                                <tr key={d.DepartmentID} className="text-center align-middle">
                                    <td>{d.Name}</td>
                                    <td>{d.MemberCount}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <span className={d.BlockedToolCount ? 'text-danger me-2' : 'text-muted me-2'}>
                                            {d.BlockedToolCount ? `${d.BlockedToolCount} blocked` : 'All tools'}
                                        </span>
                                        <Button size="sm" variant="outline-primary" onClick={() => openToolsModal('department', d)}>Tools</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>

                <Card className="shadow-sm">
                    <Card.Header><strong>Active Connections</strong></Card.Header>
                    <Table hover responsive className="mb-0">
                        <thead className="bg-light text-center">
                            <tr>
                                <th>User</th>
                                <th>App / Key</th>
                                <th>Type</th>
                                <th>Connected</th>
                                <th>Last Used</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {connections.length === 0 && (
                                <tr><td colSpan={6} className="text-center text-muted">No active connections</td></tr>
                            )}
                            {connections.map(c => (
                                <tr key={c.TokenId} className="text-center align-middle">
                                    <td>{c.Username}</td>
                                    <td>{c.Name}</td>
                                    <td><Badge bg={c.Kind === 'key' ? 'warning' : 'primary'} text={c.Kind === 'key' ? 'dark' : undefined}>{c.Kind === 'key' ? 'Personal key' : 'Login'}</Badge></td>
                                    <td>{formatDate(c.CreatedAt)}</td>
                                    <td>{formatDate(c.LastUsedAt)}</td>
                                    <td>
                                        <Button size="sm" variant="outline-danger" onClick={() => setConnectionToRevoke(c)}>Revoke</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            </Container>

            {/* Tool restrictions for one person or one department */}
            <Modal show={!!toolsTarget} onHide={() => setToolsTarget(null)} size="lg" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isUserTarget ? 'Tools for' : 'Department tools:'} {toolsTarget?.name}{' '}
                        {isUserTarget ? (
                            <Badge bg={ACCESS_BADGE[targetAccess] || 'secondary'} className="fs-6 align-middle">
                                {ACCESS_OPTIONS.find(opt => opt.value === targetAccess)?.label}
                            </Badge>
                        ) : (
                            <Badge bg="dark" className="fs-6 align-middle">Department</Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isUserTarget ? (
                        <p className="text-muted small">
                            {toolsDepartmentName
                                ? <>This person is in <strong>{toolsDepartmentName}</strong>, so that department's blocked tools apply to them. </>
                                : <>This person has no department, so only their own settings apply. </>}
                            <strong>Untick a tool to block it</strong> for them, or <strong>tick a department-blocked tool to allow it just for them</strong>.
                            Blocked tools disappear from their Claude/Grok. New tools appear here automatically once deployed.
                        </p>
                    ) : (
                        <p className="text-muted small">
                            Applies to <strong>every member</strong> of {toolsTarget?.name}. <strong>Untick a tool to block it</strong> for the whole department.
                            You can still allow or block tools for one person from their own Tools window. Members with read-only access
                            never get write tools anyway.
                        </p>
                    )}
                    <Form.Control
                        className="mb-3"
                        placeholder="Search tools..."
                        value={toolSearch}
                        onChange={e => setToolSearch(e.target.value)}
                    />
                    {toolsLoading ? (
                        <div className="text-center text-muted py-4">Loading...</div>
                    ) : ['read', 'write'].map(type => {
                        const style = TOOL_TYPE_STYLE[type];
                        const ofType = tools.filter(t => t.type === type);
                        const search = toolSearch.trim().toLowerCase();
                        const visible = ofType.filter(t => !search || `${t.name} ${t.title} ${t.description}`.toLowerCase().includes(search));
                        const notAvailable = isUserTarget && type === 'write' && targetAccess !== 'write';
                        const allowedCount = ofType.filter(t => isToolAllowed(t.name)).length;
                        return (
                            <div key={type} className="mb-4" style={{ borderLeft: `4px solid ${style.accent}`, paddingLeft: 12 }}>
                                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                    <div>
                                        <strong style={{ color: style.accent }}>
                                            {type === 'read' ? 'Read tools' : 'Write tools'}
                                        </strong>
                                        <span className="text-muted small ms-2">
                                            {type === 'read' ? 'only look things up' : 'create or change data'}
                                            {' · '}{notAvailable ? 'not available' : `${allowedCount} of ${ofType.length} allowed`}
                                        </span>
                                    </div>
                                    {!notAvailable && (
                                        <div>
                                            <Button size="sm" variant="outline-success" className="me-2" onClick={() => setAllOfType(type, true)}>Allow all</Button>
                                            <Button size="sm" variant="outline-danger" onClick={() => setAllOfType(type, false)}>Block all</Button>
                                        </div>
                                    )}
                                </div>
                                {notAvailable && (
                                    <Alert variant="light" className="py-2 small border">
                                        This user's MCP access is <strong>{ACCESS_OPTIONS.find(opt => opt.value === targetAccess)?.label}</strong>, so
                                        they never get write tools. Set them to <strong>Read &amp; write</strong> to use these.
                                    </Alert>
                                )}
                                {visible.length === 0 && <div className="text-muted small">No tools match.</div>}
                                {visible.map(tool => {
                                    const fromDepartment = isUserTarget && departmentBlocked.has(tool.name);
                                    const personallyBlocked = toolSets.blocked.has(tool.name);
                                    const exception = toolSets.allowed.has(tool.name);
                                    return (
                                        <div
                                            key={tool.name}
                                            className="px-2 py-1 mb-1 rounded"
                                            style={{ background: style.background, opacity: notAvailable ? 0.5 : 1 }}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                id={`tool-${tool.name}`}
                                                disabled={notAvailable}
                                                checked={!notAvailable && isToolAllowed(tool.name)}
                                                onChange={() => toggleTool(tool.name)}
                                                label={
                                                    <span>
                                                        <code style={{ color: 'inherit' }}>{tool.name}</code>
                                                        {fromDepartment && !exception && (
                                                            <Badge bg="secondary" className="ms-2" title="Blocked for the whole department. Tick it to allow it for this person only.">
                                                                blocked by {toolsDepartmentName}
                                                            </Badge>
                                                        )}
                                                        {fromDepartment && exception && (
                                                            <Badge bg="info" className="ms-2" title="The department blocks this tool, but it is allowed for this person.">
                                                                allowed for this person only
                                                            </Badge>
                                                        )}
                                                        {!fromDepartment && personallyBlocked && isUserTarget && (
                                                            <Badge bg="danger" className="ms-2">blocked for this person</Badge>
                                                        )}
                                                        {!tool.labeled && (
                                                            <Badge bg="warning" text="dark" className="ms-2" title="No read/write label in the server code, so it is treated as a write tool">
                                                                unlabeled
                                                            </Badge>
                                                        )}
                                                        {tool.description && (
                                                            <div className="text-muted small" style={{ lineHeight: 1.3 }}>
                                                                {tool.description.length > 130 ? `${tool.description.slice(0, 130)}...` : tool.description}
                                                            </div>
                                                        )}
                                                    </span>
                                                }
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </Modal.Body>
                <Modal.Footer>
                    <span className="text-muted small me-auto">{blockedNowCount} tool{blockedNowCount === 1 ? '' : 's'} blocked</span>
                    <Button variant="secondary" onClick={() => setToolsTarget(null)}>Cancel</Button>
                    <Button variant="primary" disabled={toolsLoading || toolsSaving} onClick={saveTools}>
                        {toolsSaving ? 'Saving...' : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Create personal key */}
            <Modal show={showKeyModal} onHide={closeKeyModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Create Personal Key</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {createdKey ? (
                        <>
                            <Alert variant="warning" className="small">
                                Copy this key now — it won't be shown again. Paste it into the app's Bearer token / API key field.
                            </Alert>
                            <InputGroup>
                                <Form.Control readOnly value={createdKey} onFocus={e => e.target.select()} />
                                <Button variant="outline-secondary" onClick={() => navigator.clipboard.writeText(createdKey)}>Copy</Button>
                            </InputGroup>
                        </>
                    ) : (
                        <>
                            <p className="text-muted small">
                                For apps that can't do the normal sign-in (for example Grok if its login doesn't open).
                                The key acts as the chosen user, with that user's MCP access.
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label>User</Form.Label>
                                <Form.Select value={keyUserId} onChange={e => setKeyUserId(e.target.value)}>
                                    <option value="">Select user</option>
                                    {users.map(u => (
                                        <option key={u.UserID} value={u.UserID}>{u.Username}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Key name</Form.Label>
                                <Form.Control placeholder="e.g. Grok - Alif" value={keyName} onChange={e => setKeyName(e.target.value)} />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeKeyModal}>Close</Button>
                    {!createdKey && (
                        <Button variant="primary" disabled={!keyUserId || !keyName.trim()} onClick={handleCreateKey}>Create Key</Button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Revoke confirmation */}
            <Modal show={!!connectionToRevoke} onHide={() => setConnectionToRevoke(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Revoke Connection</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Disconnect <strong>{connectionToRevoke?.Name}</strong> for <strong>{connectionToRevoke?.Username}</strong>?
                    It stops working immediately; they would have to sign in again to reconnect.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setConnectionToRevoke(null)}>Cancel</Button>
                    <Button variant="danger" onClick={confirmRevoke}>Revoke</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default McpAccess;
